import { useState } from "react";
import { shouldUpperCase } from "../../global/textCase";
import {
  displayTwoDecimalDigits,
  sanitizeDecimalInput,
} from "../../global/Utils";

export const InputField = ({
  label,
  name,
  type = "text",
  required,
  value,
  error = "",
  onChange,
  disabled = false,
  readOnly = false,
  min,
  max,
  step,
  placeholder,
  isUpperCase = true,
  onFocus,
  onBlur,
}) => {
  // Capital letters everywhere, except for values whose casing is significant
  // (tokens, credentials, URLs) and types that carry no letters.
  const upperCase = isUpperCase && shouldUpperCase({ name, type });

  const handleChange = (event) => {
    if (!onChange) return;
    if (upperCase && typeof event?.target?.value === "string") {
      event.target.value = event.target.value.toUpperCase();
    }
    onChange(event);
  };

  return (
    <div className="w-full">
      <label className="block mb-1 font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        readOnly={readOnly}
        className={`${upperCase ? "uppercase" : ""} w-full px-4 py-2 border rounded-md focus:outline-none disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${
          error ? "border-red-500" : "border-gray-300 focus:border-blue-500"
        }`}
      />

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

/**
 * A money box the user types into. While it is being typed in it shows what
 * was typed - no more than two decimals are taken - and otherwise the value
 * rounded and written with exactly two decimals ("250.50"). Formatting while
 * typing would turn "1" into "1.00" under the cursor.
 */
export const MoneyInputField = ({ value, onChange, ...props }) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = (event) => {
    const typed = event?.target?.value;
    const limited = sanitizeDecimalInput(typed);
    if (typeof typed === "string" && limited !== typed) {
      event.target.value = limited;
    }
    if (onChange) onChange(event);
  };

  return (
    <InputField
      step="0.01"
      {...props}
      type="number"
      value={isEditing ? (value ?? "") : displayTwoDecimalDigits(value)}
      onChange={handleChange}
      onFocus={() => setIsEditing(true)}
      onBlur={() => setIsEditing(false)}
    />
  );
};

export const MobileNumberField = ({
  label,
  name,
  value = "",
  error = "",
  required,
  onChange,
  disabled = false,
  readOnly = false,
}) => {
  // Show formatted value (remove +92 for display)
  const displayValue = value
    ?.replace("+92", "")
    ?.replace(/^(\d{3})(\d{0,7})$/, (_, p1, p2) => (p2 ? `${p1}-${p2}` : p1));

  return (
    <div className="w-full">
      <label className="block mb-1 font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="flex">
        {/* +92 Prefix */}
        <span className="px-3 py-2 bg-gray-100 border border-r-0 rounded-l-md text-gray-600">
          +92
        </span>

        {/* Input */}
        <input
          type="text"
          name={name}
          value={displayValue || ""}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          placeholder="XXX-XXXXXXX"
          className={`w-full px-3 py-2 border rounded-r-md focus:outline-none ${
            error ? "border-red-500" : "border-gray-300 focus:border-blue-500"
          }`}
        />
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};
